import type { NativeVideoCodec } from '../../native-video/NativeVideoProtocol';

/** Wire limits are independent of smaller limits negotiated by the transport. */
export const WC_LIMITS = Object.freeze({
  headerBytes: 56, messageBytes: 32 * 1024, frameBytes: 2 * 1024 * 1024,
  fragmentCount: 4096, windowBytes: 4 * 1024 * 1024,
  controlBytes: 16 * 1024, feedbackBytes: 8 * 1024,
  maxId: 0xffffffff, maxTime: Number.MAX_SAFE_INTEGER,
});
/**
 * Video is timed rather than retransmit-limited: a fragment older than a few frames is of no use
 * whether it is still waiting in the host's transport or was lost on the way. The lifetime lets the
 * transport drop what it has not sent yet when the link rate collapses (the host's SCTP honours it
 * before the first transmission), and it keeps the transport from retransmitting a window of stale
 * video ahead of the reconfiguration that collapse provokes. Repair of what the receiver actually
 * misses is the sender's own, driven by feedback. Feedback is tiny and superseded every report.
 */
export const WC_VIDEO_LIFETIME_MS = 200;
export const WC_CHANNELS = Object.freeze({
  control: { label: 'playcast-wc-control-v1', ordered: true },
  video: { label: 'playcast-wc-video-v1', ordered: false, maxPacketLifeTime: WC_VIDEO_LIFETIME_MS },
  feedback: { label: 'playcast-wc-feedback-v1', ordered: false, maxRetransmits: 0 },
});

/** SDR only. Actual codec/profile/color values must come from encoded headers. */
export type WcConfig = {
  epoch: number; codec: NativeVideoCodec; codecString: string;
  format: 'annexb' | 'av1-obu'; width: number; height: number;
  fps: number; bitDepth: 8;
  primaries: 'bt709'; transfer: 'bt709'; matrix: 'bt709'; fullRange: boolean;
};
export type WcControl =
  | { v: 1; type: 'configure'; session: string; config: WcConfig }
  | { v: 1; type: 'configured'; session: string; epoch: number }
  | { v: 1; type: 'rejected'; session: string; epoch: number; reason: string }
  | { v: 1; type: 'keyframe'; session: string; epoch: number; afterFrameId: number }
  | { v: 1; type: 'source'; session: string; epoch: number; latestFrameId: number; state: 'active' | 'idle' | 'paused' }
  | { v: 1; type: 'ping'; session: string; probeId: number; hostSendUs: number }
  | { v: 1; type: 'pong'; session: string; probeId: number; hostSendUs: number; clientReceiveUs: number; clientSendUs: number }
  | { v: 1; type: 'close'; session: string; reason: string };

/** Cumulative epoch counters. Receiving an ACK does not retire its frame or add window credit. */
export type WcFeedback = {
  v: 1; type: 'feedback'; session: string; epoch: number; sequence: number;
  clientNowUs: number; highestPacketId: number;
  receivedRanges: Array<[number, number]>;
  receivedBytes: number;
  arrivalSamples: Array<{ packetId: number; arrivalUs: number }>;
  missingFragments: Array<{ frameId: number; indices: number[]; remainingUs: number }>;
  retiredThroughFrameId: number; decodedFrameId: number; renderedFrameId: number;
  receiverWindowBytes: number; reassemblyBytes: number; decodeQueueSize: number;
  decoderInFlight: number; renderPending: number; oldestPendingAgeUs: number;
  decodeP95Us: number; renderIntervalP95Us: number; visible: boolean; needsKeyframe: boolean;
};

/** Recoverable worker signals. `failed` stays reserved for an uncaught worker exception. */
export type WcWorkerRecoveryMessage =
  | { type: 'stalled'; epoch: number; sinceMs: number }
  | { type: 'needsKeyframe'; epoch: number; reason: 'gap' | 'decode-error' | 'epoch' }
  | { type: 'presented'; epoch: number; presentedFrames: number; maxGapMs: number; latencyUs: number[] }
  | { type: 'unsupported'; codec: string; message: string };

/** Complete Annex B AU or AV1 temporal unit fragments; timestamps are host-relative microseconds. */
export type WcFragment = {
  epoch: number; frameId: number; fragmentIndex: number; fragmentCount: number;
  frameBytes: number; fragmentOffset: number; captureUs: number; sentUs: number;
  durationUs: number; packetId: number; key: boolean; payload: Uint8Array;
};
